/**
 * Font Value Utilities - Enhanced for Resolution-Based Scaling
 * 
 * BACKWARD COMPATIBLE: Existing code will continue to work
 * NEW FEATURE: Optional scaling support for 4K displays
 */

import { CSS_VARIABLES, DEFAULT_SCALE_VALUES } from '../theme/resolutionConfig';

/**
 * Convert pixels to rem (ENHANCED VERSION)
 * @param {number} value - Pixel value to convert
 * @param {boolean} enableScaling - Apply CSS variable scaling (default: false for backward compatibility)
 * @returns {string} - Rem value or calc() with scaling
 * 
 * Examples:
 * pxToRem(16) → "1rem" (backward compatible)
 * pxToRem(16, true) → "calc(1rem * var(--font-scale, 1))" (with scaling)
 */
export function pxToRem(value, enableScaling = false) {
  const remValue = value / 16;
  
  if (!enableScaling) {
    // Backward compatible behavior
    return `${remValue}rem`;
  }
  
  // New scaling behavior - uses CSS variables
  return `calc(${remValue}rem * var(${CSS_VARIABLES.FONT_SCALE}, ${DEFAULT_SCALE_VALUES.fontScale}))`;
}

/**
 * Convert pixels to rem with forced scaling (always scales)
 * Use this for new components that need resolution scaling
 */
export function pxToRemScaled(value) {
  return pxToRem(value, true);
}

/**
 * Responsive font sizes (ENHANCED VERSION)
 * Now supports 4K displays with optional scaling
 * 
 * @param {Object} sizes - Font sizes for breakpoints
 * @param {number} sizes.sm - Small (1024px+)
 * @param {number} sizes.md - Medium (1536px+)
 * @param {number} sizes.lg - Large (1920px+ FHD)
 * @param {number} [sizes.xl] - Extra Large (2560px+ QHD)
 * @param {number} [sizes.xxl] - 2X Large (3200px+ 4K)
 * @param {number} [sizes.xxxl] - 3X Large (3840px+ 4K+ Assembly Line)
 * @param {boolean} [enableScaling=false] - Apply CSS variable scaling (default: false for backward compatibility)
 * @returns {Object} - Media query object for Material-UI theme
 * 
 * BACKWARD COMPATIBLE: Existing calls with { sm, md, lg } work unchanged
 * NEW: Add xl, xxl, xxxl for 4K support
 */
export function responsiveFontSizes({ sm, md, lg, xl, xxl, xxxl }, enableScaling = false) {
  // Calculate auto values for missing breakpoints (intelligent scaling)
  const xlCalc = xl || lg * 1.25;   // 25% larger than lg
  const xxlCalc = xxl || lg * 1.5;  // 50% larger than lg
  const xxxlCalc = xxxl || lg * 2.0; // 2x larger than lg (for 7-meter viewing)
  
  return {
    // Original breakpoints (BACKWARD COMPATIBLE)
    '@media (min-width:1024px)': {
      fontSize: enableScaling ? pxToRem(sm, true) : pxToRem(sm),
      letterSpacing: sm / lg,
    },
    '@media (min-width:1536px)': {
      fontSize: enableScaling ? pxToRem(md, true) : pxToRem(md),
      letterSpacing: md / lg,
    },
    // Updated from 2048px to 1920px (FHD standard)
    '@media (min-width:1920px)': {
      fontSize: enableScaling ? pxToRem(lg, true) : pxToRem(lg),
      letterSpacing: 2.5,
    },
    // NEW: QHD support (2560px)
    '@media (min-width:2560px)': {
      fontSize: enableScaling ? pxToRem(xlCalc, true) : pxToRem(xlCalc),
      letterSpacing: 2.5,
    },
    // NEW: 4K support (3200px)
    '@media (min-width:3200px)': {
      fontSize: enableScaling ? pxToRem(xxlCalc, true) : pxToRem(xxlCalc),
      letterSpacing: 2.5,
    },
    // NEW: 4K+ Assembly Line support (3840px)
    '@media (min-width:3840px)': {
      fontSize: enableScaling ? pxToRem(xxxlCalc, true) : pxToRem(xxxlCalc),
      letterSpacing: 2.5,
    },
  };
}

/**
 * Responsive font sizes with scaling ENABLED
 * Use this for new components that need resolution-aware scaling
 */
export function responsiveFontSizesScaled(sizes) {
  return responsiveFontSizes(sizes, true);
}

/**
 * Get responsive font sizes specifically for assembly line (4K optimized)
 * Automatically generates appropriate sizes for large displays
 * 
 * @param {number} baseFontSize - Base font size in pixels (at 1920px FHD)
 * @returns {Object} - Complete responsive font size object
 */
export function assemblyLineFontSizes(baseFontSize) {
  return responsiveFontSizes({
    sm: baseFontSize * 0.85,  // Slightly smaller for laptops
    md: baseFontSize * 0.95,  // Medium laptops
    lg: baseFontSize,         // Base size at FHD
    xl: baseFontSize * 1.3,   // QHD
    xxl: baseFontSize * 1.8,  // 4K
    xxxl: baseFontSize * 2.5, // 4K+ (visible from 7 meters)
  }, true); // Scaling enabled
}

/**
 * Legacy export for backward compatibility
 * Keeps old behavior: uses old breakpoint at 2048px
 */
export function responsiveFontSizesLegacy({ sm, md, lg }) {
  return {
    '@media (min-width:1024px)': {
      fontSize: pxToRem(sm),
      letterSpacing: sm / lg,
    },
    '@media (min-width:1536px)': {
      fontSize: pxToRem(md),
      letterSpacing: md / lg,
    },
    '@media (min-width:2048px)': {
      fontSize: pxToRem(lg),
      letterSpacing: 2.5,
    },
  };
}

// Export default for backward compatibility
export default {
  pxToRem,
  pxToRemScaled,
  responsiveFontSizes,
  responsiveFontSizesScaled,
  assemblyLineFontSizes,
  responsiveFontSizesLegacy,
};